import { useCallback, useEffect, useMemo, useState } from "react";
import { request } from "../lib/api";
import type { DashboardFilters, Resource } from "./types";

function pathWithFilters(path: string, district: string, centre: string, module: string) {
  const [pathname, current = ""] = path.split("?");
  const params = new URLSearchParams(current);
  Object.entries({ district, centre, module }).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ""}`;
}

export function useDashboardResource<T>(
  path: string,
  filters: DashboardFilters,
  pollMilliseconds = 0,
): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const filteredPath = useMemo(
    () => pathWithFilters(path, filters.district, filters.centre, filters.module),
    [path, filters.district, filters.centre, filters.module],
  );
  const load = useCallback((showSkeleton: boolean) => {
    if (showSkeleton) setLoading(true);
    request<T>(filteredPath)
      .then((value) => { setData(value); setError(""); })
      .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : "Request failed"))
      .finally(() => setLoading(false));
  }, [filteredPath]);

  useEffect(() => {
    const initial = window.setTimeout(() => load(true), 0);
    const timer = pollMilliseconds ? window.setInterval(() => load(false), pollMilliseconds) : 0;
    return () => { window.clearTimeout(initial); if (timer) window.clearInterval(timer); };
  }, [load, pollMilliseconds, attempt]);

  return { data, loading, error, retry: () => setAttempt((value) => value + 1) };
}
